'use strict';

const core = require('./analytics_core');

const CONSENT_COOKIE = 'ms_consent';
const FIRST_TOUCH_COOKIE = 'ms_ft';
const LAST_TOUCH_COOKIE = 'ms_lt';
const CONSENT_DAYS = 180;
const FIRST_TOUCH_DAYS = 180;
const LAST_TOUCH_DAYS = 30;

const meta = document.querySelector('meta[name="ms-analytics"]');
const config = meta ? meta.dataset : {};
const firedOnce = new Set();
let gaLoaded = false;

function readCookie(name) {
    const prefix = name + '=';
    const found = document.cookie.split('; ').find(function (c) { return c.indexOf(prefix) === 0; });
    return found ? found.substring(prefix.length) : null;
}

function writeCookie(name, value, days) {
    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie = name + '=' + value + '; path=/; max-age=' + (days * 86400) + '; SameSite=Lax' + secure;
}

function deleteCookie(name) {
    document.cookie = name + '=; path=/; max-age=0; SameSite=Lax';
    const parts = location.hostname.split('.');
    if (parts.length >= 2) {
        document.cookie = name + '=; path=/; max-age=0; domain=.' + parts.slice(-2).join('.');
    }
}

function readJsonCookie(name) {
    try {
        const raw = readCookie(name);
        return raw ? JSON.parse(decodeURIComponent(raw)) : null;
    } catch (e) {
        return null;
    }
}

function currentConsent() {
    const raw = readCookie(CONSENT_COOKIE);
    return raw ? core.parseConsent(raw) : null;
}

function captureAttribution() {
    const touch = core.resolveTouch(location.href, document.referrer);
    const merged = core.mergeTouches(readJsonCookie(FIRST_TOUCH_COOKIE), readJsonCookie(LAST_TOUCH_COOKIE), touch, new Date().toISOString());
    writeCookie(FIRST_TOUCH_COOKIE, encodeURIComponent(JSON.stringify(merged.first)), FIRST_TOUCH_DAYS);
    writeCookie(LAST_TOUCH_COOKIE, encodeURIComponent(JSON.stringify(merged.last)), LAST_TOUCH_DAYS);
}

function consentState(granted) {
    return granted ? 'granted' : 'denied';
}

function gtag() {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(arguments);
}

function applyGoogleConsent(mode, consent) {
    gtag('consent', mode, {
        analytics_storage: consentState(consent.analytics),
        ad_storage: consentState(consent.marketing),
        ad_user_data: consentState(consent.marketing),
        ad_personalization: consentState(consent.marketing)
    });
}

function loadGoogleAnalytics(consent) {
    if (gaLoaded || !config.gaId) {
        return;
    }
    gaLoaded = true;

    applyGoogleConsent('default', consent);
    gtag('js', new Date());
    gtag('config', config.gaId, {
        send_page_view: config.pageView === '1',
        page_location: core.sanitizeLocation(location.href),
        page_referrer: document.referrer ? core.sanitizeLocation(document.referrer) : ''
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(config.gaId);
    document.head.appendChild(script);
}

function newEventId() {
    if (window.crypto && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return Date.now().toString(36) + Math.random().toString(36).substring(2);
}

function baseParams() {
    const first = readJsonCookie(FIRST_TOUCH_COOKIE) || {};
    const last = readJsonCookie(LAST_TOUCH_COOKIE) || {};
    return {
        page_type: config.pageType,
        page_slug: config.pageSlug,
        page_intent: config.pageIntent,
        page_segment: config.pageSegment,
        locale: config.locale,
        is_authenticated: config.authenticated,
        device_category: window.matchMedia('(max-width: 767.98px)').matches ? 'mobile' : 'desktop',
        traffic_source: last.s,
        traffic_medium: last.m,
        traffic_campaign: last.c,
        first_touch_source: first.s,
        first_touch_medium: first.m,
        first_touch_campaign: first.c
    };
}

function track(name, params) {
    const consent = currentConsent();
    if (!consent || !consent.analytics || !gaLoaded || !core.isAllowedEvent(name)) {
        return;
    }
    const payload = core.sanitizeEventParams(Object.assign({ event_id: newEventId() }, baseParams(), params));
    gtag('event', name, payload);
}

function trackOnce(key, name, params) {
    if (!firedOnce.has(key)) {
        firedOnce.add(key);
        track(name, params);
    }
}

function onClick(event) {
    const target = event.target.closest ? event.target : null;
    if (!target) {
        return;
    }

    const cta = target.closest('[data-cta-name]');
    if (cta) {
        const params = { cta_name: cta.dataset.ctaName, cta_position: cta.dataset.ctaPosition };
        if (cta.dataset.ctaPosition === 'hero') {
            track('hero_cta_click', params);
        } else if (cta.dataset.ctaPosition === 'final') {
            track('final_cta_click', params);
        }
        if (cta.dataset.ctaPosition === 'result') {
            track('calculator_result_saved', params);
        } else if (cta.dataset.ctaPosition === 'share') {
            track('calculator_share', params);
        }
        const href = cta.getAttribute('href') || '';
        if (href.indexOf('/go/telegram') !== -1) {
            track('telegram_click', params);
        } else if (href.indexOf('/register') !== -1 || href.indexOf('/Register') !== -1) {
            track('signup_start', params);
        }
    }

    const tool = target.closest('[data-related-tool]');
    if (tool) {
        track('related_tool_click', { cta_name: tool.dataset.relatedTool, cta_position: 'related' });
    }

    const faq = target.closest('[data-analytics-faq] .accordion-button');
    if (faq && !faq.classList.contains('collapsed')) {
        track('faq_expand', { cta_name: faq.dataset.faqId || faq.getAttribute('aria-controls') });
    }
}

document.addEventListener('monsave:guide-calc-used', function (event) {
    trackOnce('guide_calculator_use', 'guide_calculator_use', { cta_name: event.detail.mode, cta_position: 'guide_calc' });
});

function observeProductProof() {
    const proof = document.querySelector('[data-product-proof]');
    if (!proof || !('IntersectionObserver' in window)) {
        return;
    }
    const observer = new IntersectionObserver(function (entries) {
        if (entries.some(function (e) { return e.isIntersecting; })) {
            trackOnce('proof', 'product_proof_view');
            observer.disconnect();
        }
    }, { threshold: 0.5 });
    observer.observe(proof);
}

function startTracking(consent) {
    // On application pages the script only serves the cookie settings.
    if (!consent.analytics || config.collect !== '1') {
        return;
    }
    captureAttribution();
    loadGoogleAnalytics(consent);

    if (config.pageType === 'intent_landing') {
        trackOnce('landing', 'landing_view');
    }
    if (config.event === 'sign_up') {
        trackOnce('signup', 'sign_up');
    }
    if (config.event === 'activation_completed') {
        trackOnce('activation', 'activation_completed', { activation_type: config.activationType });
    }
    observeProductProof();
}

function revokeTracking() {
    [FIRST_TOUCH_COOKIE, LAST_TOUCH_COOKIE].forEach(deleteCookie);
    document.cookie.split('; ').forEach(function (c) {
        const name = c.split('=')[0];
        if (name === '_ga' || name.indexOf('_ga_') === 0) {
            deleteCookie(name);
        }
    });
    if (config.gaId) {
        window['ga-disable-' + config.gaId] = true;
    }
}

function saveConsent(analytics, marketing) {
    const previous = currentConsent();
    writeCookie(CONSENT_COOKIE, core.serializeConsent(analytics, marketing, new Date().toISOString()), CONSENT_DAYS);
    const consent = { analytics: analytics, marketing: marketing };

    if (gaLoaded) {
        applyGoogleConsent('update', consent);
    }
    if (analytics) {
        if (config.gaId) {
            delete window['ga-disable-' + config.gaId];
        }
        startTracking(consent);
    } else if (!previous || previous.analytics) {
        revokeTracking();
    }
}

function initBanner() {
    const banner = document.getElementById('consentBanner');
    if (!banner) {
        return;
    }
    const analyticsInput = document.getElementById('consentAnalytics');
    const marketingInput = document.getElementById('consentMarketing');
    const details = document.getElementById('consentDetails');
    const close = function () { banner.hidden = true; };

    banner.querySelector('[data-consent-accept]').addEventListener('click', function () { saveConsent(true, true); close(); });
    banner.querySelector('[data-consent-reject]').addEventListener('click', function () { saveConsent(false, false); close(); });
    banner.querySelector('[data-consent-customize]').addEventListener('click', function () { details.hidden = !details.hidden; });
    banner.querySelector('[data-consent-save]').addEventListener('click', function () {
        saveConsent(analyticsInput.checked, marketingInput.checked);
        close();
    });

    document.querySelectorAll('[data-consent-open]').forEach(function (link) {
        link.addEventListener('click', function (event) {
            event.preventDefault();
            const consent = currentConsent();
            analyticsInput.checked = !!(consent && consent.analytics);
            marketingInput.checked = !!(consent && consent.marketing);
            details.hidden = false;
            banner.hidden = false;
        });
    });

    // Application pages collect nothing, so they only open the window from the footer link.
    if (!currentConsent() && config.pageView === '1') {
        banner.hidden = false;
    }
}

function init() {
    initBanner();
    document.addEventListener('click', onClick);
    const consent = currentConsent();
    if (consent) {
        startTracking(consent);
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
} else {
    init();
}
